import { createApiV2 } from "@/apiv2/common";
import { getPartialInvoices as getPartialInvoicesApi } from "@/apiv2/partialInvoice";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";

import { MODULE } from "./constants";
import * as selectors from "./payment-status.selectors";

const getPaymentsApi = ({ customerUid, orderUid }) =>
  createApiV2("GET", `customers/${customerUid}/orders/${orderUid}/payments/`);

const postRecordPaymentApi = ({ customerUid, orderUid }) =>
  createApiV2("POST", `customers/${customerUid}/orders/${orderUid}/payments/`);

const deletePaymentApi = ({ customerUid, orderUid, paymentUid }) =>
  createApiV2("DELETE", `customers/${customerUid}/orders/${orderUid}/payments/${paymentUid}/`);

const restorePaymentApi = ({ customerUid, orderUid, paymentUid }) =>
  createApiV2("PATCH", `customers/${customerUid}/orders/${orderUid}/payments/${paymentUid}/undestroy/`);

const getPaymentMethodsApi = () => createApiV2("GET", "accounting/payment-methods/");

const getPaymentAccountsApi = () => createApiV2("GET", "accounting/payment-accounts/");

export const [
  setPayments,
  setInvoiceTotal,
  setTotalPaid,
  setBalanceDue,
  setFetchStatus,
  setRecordStatus,
  setDeleteStatus,
  setIsRecordModalOpen,
  setDeleteTarget,
  setPaymentStatus,
  setPaymentMethodOptions,
  setPaymentAccountOptions,
  setPartialInvoices,
  setOrderTotal,
  setOrderOutstandingBalance,
  setRestoreStatus,
  setRestoreError,
  setDeleteError,
] = createSetterActions(MODULE, [
  "payments",
  "invoiceTotal",
  "totalPaid",
  "balanceDue",
  "fetchStatus",
  "recordStatus",
  "deleteStatus",
  "isRecordModalOpen",
  "deleteTarget",
  "paymentStatus",
  "paymentMethodOptions",
  "paymentAccountOptions",
  "partialInvoices",
  "orderTotal",
  "orderOutstandingBalance",
  "restoreStatus",
  "restoreError",
  "deleteError",
]);

export const reset = createAction(`${MODULE}/RESET`);

export const fetchPaymentMethods = () => async (dispatch, getState) => {
  const paymentMethodOptions = selectors.getPaymentMethodOptions(getState());
  if (paymentMethodOptions?.length) {
    return;
  }

  try {
    const { data } = await getPaymentMethodsApi()();
    dispatch(setPaymentMethodOptions(data));
  } catch (err) {
    handleError(err, dispatch);
  }
};

export const fetchPaymentAccounts = () => async (dispatch, getState) => {
  const paymentAccountOptions = selectors.getPaymentAccountOptions(getState());
  if (paymentAccountOptions?.length) {
    return;
  }

  try {
    const { data } = await getPaymentAccountsApi()();
    dispatch(setPaymentAccountOptions(data.map((acct) => ({ value: acct.id, label: acct.name }))));
  } catch (err) {
    handleError(err, dispatch);
  }
};

export const fetchPayments =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    try {
      dispatch(setFetchStatus(STATUS.PENDING));
      dispatch(setPaymentStatus("Unpaid"));
      const { data } = await getPaymentsApi({ customerUid, orderUid })();
      dispatch(setPayments(data.results));
      dispatch(setInvoiceTotal(data.invoiceTotal));
      dispatch(setTotalPaid(data.totalPaid));
      dispatch(setBalanceDue(data.invoiceOutstandingBalance));
      dispatch(setOrderTotal(data.orderTotal));
      dispatch(setOrderOutstandingBalance(data.orderOutstandingBalance));
      dispatch(setPaymentStatus(data.paymentStatus));
      dispatch(setFetchStatus(STATUS.FULFILLED));
    } catch (err) {
      dispatch(setFetchStatus(STATUS.REJECTED));
      handleError(err, dispatch);
    }
  };

export const fetchPartialInvoices =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    try {
      const { data } = await getPartialInvoicesApi({ customerUid, orderUid })();
      const invoices = data?.results || data;
      dispatch(setPartialInvoices(invoices?.length > 0 ? invoices : null));
    } catch (err) {
      dispatch(setPartialInvoices(null));
      handleError(err, dispatch);
    }
  };

export const recordPayment =
  ({ customerUid, orderUid, payload }) =>
  async (dispatch) => {
    try {
      dispatch(setRecordStatus(STATUS.PENDING));
      await postRecordPaymentApi({ customerUid, orderUid })(undefined, payload);
      dispatch(setRecordStatus(STATUS.FULFILLED));
      dispatch(setIsRecordModalOpen(false));
      dispatch(fetchPayments({ customerUid, orderUid }));
      dispatch(fetchPartialInvoices({ customerUid, orderUid }));
    } catch (err) {
      dispatch(setRecordStatus(STATUS.REJECTED));
      throw err;
    }
  };

export const restorePayment =
  ({ customerUid, orderUid, paymentUid }) =>
  async (dispatch) => {
    try {
      dispatch(setRestoreStatus(STATUS.PENDING));
      dispatch(setRestoreError(null));
      await restorePaymentApi({ customerUid, orderUid, paymentUid })();
      dispatch(setRestoreStatus(STATUS.FULFILLED));
      dispatch(fetchPayments({ customerUid, orderUid }));
      dispatch(fetchPartialInvoices({ customerUid, orderUid }));
    } catch (err) {
      dispatch(setRestoreStatus(STATUS.REJECTED));

      if (!err?.response) {
        dispatch(setRestoreError("Failed to restore payment. Please try again."));
        return;
      }

      const errorData = err.response.data;
      if (errorData?.code === "overpayment") {
        // Structured breakdown the blocked modal renders; detail stays plain text.
        dispatch(setRestoreError(errorData.overpayment));
        return;
      }

      const detailError = errorData?.detail;
      const message =
        (Array.isArray(detailError) ? detailError[0] : detailError) ||
        "This payment cannot be restored.";
      dispatch(setRestoreError(message));
    }
  };

export const deletePayment =
  ({ customerUid, orderUid, paymentUid }) =>
  async (dispatch) => {
    try {
      dispatch(setDeleteStatus(STATUS.PENDING));
      dispatch(setDeleteError(null));
      await deletePaymentApi({ customerUid, orderUid, paymentUid })();
      dispatch(setDeleteStatus(STATUS.FULFILLED));
      dispatch(setDeleteTarget(null));
      dispatch(fetchPayments({ customerUid, orderUid }));
      dispatch(fetchPartialInvoices({ customerUid, orderUid }));
    } catch (err) {
      dispatch(setDeleteStatus(STATUS.REJECTED));

      if (!err?.response) {
        dispatch(setDeleteError("Failed to delete payment. Please try again."));
        return;
      }

      const errorData = err.response.data;
      const message =
        errorData?.detail || errorData?.message || "Failed to delete payment. Please try again.";
      dispatch(setDeleteError(message));
    }
  };
